import { format, isAfter, isBefore } from 'date-fns';
import {
  CountryCode,
  CustomerPmcPromotion,
  Lang,
  LocalizedString,
  Maybe,
  Validity,
} from 'types/generatedSchemaTypes';
import { currencyFormatter } from './commonUtils';

export type PromotionLike = {
  name?: Maybe<LocalizedString>;
  percent?: Maybe<number>;
  value?: Maybe<number>;
  validity?: Maybe<Validity>;
};

export const getPromotionText = (
  promotion: Maybe<PromotionLike> | undefined,
  country: Maybe<CountryCode> | undefined,
  preferredLanguage: Maybe<Lang> | undefined,
  labelKey: string,
  t: (key: string) => string
): string | undefined => {
  if (!promotion) {
    return undefined;
  }

  const promotionName = promotion.name?.[preferredLanguage ?? 'en'];
  if (promotionName) {
    return promotionName;
  }

  if (promotion.value) {
    return (
      t(labelKey) +
      ' ' +
      currencyFormatter(promotion.value * 0.01, country || 'SE')
    );
  }

  return t(labelKey) + ' ' + promotion.percent + '%';
};

export const getPromotionValidityText = (
  validity: Maybe<Validity> | undefined
): string | undefined => {
  const validFromText = validity?.validFrom
    ? format(new Date(validity.validFrom), 'd.M.y')
    : '';
  const validToText = validity?.validTo
    ? format(new Date(validity.validTo), 'd.M.y')
    : '';
  if (!validFromText && !validToText) {
    return undefined;
  }
  return validFromText + ' - ' + validToText;
};

export const getDiscountedAmount = (
  totalPrice: number,
  serviceFee: number,
  value: number,
  percent: number
): number => {
  const basePrice = Math.max(totalPrice - serviceFee, 0);
  const discountAmount = percent > 0 ? basePrice * percent * 0.01 : value;
  const fullDiscount = percent === 100 || (value > 0 && value >= basePrice);
  return Math.max(
    totalPrice - discountAmount - (fullDiscount ? serviceFee : 0),
    0
  );
};

export const isPromotionActive = (
  validity: Maybe<Validity> | undefined,
  referenceDate: Date
): boolean => {
  const today = new Date();
  const activeFrom = isBefore(today, referenceDate) ? referenceDate : today;
  return (
    !isAfter(new Date(validity?.validFrom || ''), activeFrom) &&
    (!validity?.validTo || isAfter(new Date(validity.validTo), today))
  );
};

export const fromCustomerPmcPromotion = (
  promotion: Maybe<CustomerPmcPromotion> | undefined
): Maybe<PromotionLike> | undefined =>
  promotion
    ? {
        name: promotion.name,
        percent: promotion.discountPercent,
        value: promotion.discountValue,
        validity: promotion.discountValidity,
      }
    : promotion;

export type PromotionPricing = {
  promotionLike: Maybe<PromotionLike> | undefined;
  hasActivePromotion: boolean;
  originalPriceValue: number | undefined;
  discountedPriceValue: number | undefined;
};

export const getPromotionPricing = (
  promotion: Maybe<CustomerPmcPromotion> | undefined,
  totalPriceCents: number | undefined,
  serviceFeeCents: number | undefined,
  referenceDate: Date
): PromotionPricing => {
  const promotionLike = fromCustomerPmcPromotion(promotion);
  const hasActivePromotion =
    !!promotionLike &&
    totalPriceCents !== undefined &&
    isPromotionActive(promotionLike.validity, referenceDate);

  const originalPriceValue =
    totalPriceCents !== undefined ? totalPriceCents / 100 : undefined;
  const discountedPriceValue =
    hasActivePromotion && originalPriceValue !== undefined
      ? getDiscountedAmount(
          originalPriceValue,
          (serviceFeeCents ?? 0) / 100,
          (promotionLike?.value ?? 0) / 100,
          promotionLike?.percent || 0
        )
      : undefined;

  return {
    promotionLike,
    hasActivePromotion,
    originalPriceValue,
    discountedPriceValue,
  };
};
